import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinancialReport, AgingBucket, FinConsolidationRun } from '@/types/finance';
import { notify } from '@/core/toast/notify';

function PaginatedReportTable({ report, title }: { report: FinancialReport | null; title: string }) {
  const lines = report?.lines ?? [];
  const { rows: paginatedLines, tablePaginationProps: linesPaginationProps } = useClientPaginatedRows(lines);

  return (
    <div className="card p-4">
      <h3 className="font-semibold mb-3">{title}</h3>
      {!report || lines.length === 0 ? (
        <p className="text-sm text-slate-500">No data yet. Post journals or run operational events (POS, procurement).</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <tbody>
              {paginatedLines.map((line, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="py-2">{line.label}</td>
                  <td className="py-2 text-right font-mono">{line.amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold">
                <td className="py-2">Total</td>
                <td className="py-2 text-right font-mono">{report.total?.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
          <TablePagination {...linesPaginationProps} hasRows={lines.length > 0} />
        </>
      )}
    </div>
  );
}

export function FinanceReportsPage() {
  const [pl, setPl] = useState<FinancialReport | null>(null);
  const [bs, setBs] = useState<FinancialReport | null>(null);
  const [cf, setCf] = useState<FinancialReport | null>(null);
  const [ar, setAr] = useState<FinancialReport | null>(null);
  const [consolidation, setConsolidation] = useState<FinancialReport | null>(null);
  const [apBuckets, setApBuckets] = useState<AgingBucket[]>([]);
  const [consolidationRuns, setConsolidationRuns] = useState<FinConsolidationRun[]>([]);
  const [fiscalYear, setFiscalYear] = useState(String(new Date().getFullYear()));
  const [fiscalPeriod, setFiscalPeriod] = useState(String(new Date().getMonth() + 1));
  const [closing, setClosing] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const { rows: paginatedApBuckets, tablePaginationProps: apBucketsPaginationProps } =
    useClientPaginatedRows(apBuckets);
  const { rows: paginatedConsolidationRuns, tablePaginationProps: consolidationRunsPaginationProps } =
    useClientPaginatedRows(consolidationRuns);

  useEffect(() => {
    financeApi.profitAndLoss().then(setPl).catch((e) => showApiError(e, 'Failed to load P&L'));
    financeApi.balanceSheet().then(setBs).catch((e) => showApiError(e, 'Failed to load balance sheet'));
    financeApi.cashFlow().then(setCf).catch((e) => showApiError(e, 'Failed to load cash flow'));
    financeApi.arAging().then(setAr).catch((e) => showApiError(e, 'Failed to load AR aging'));
    financeApi.apAgingBuckets().then(setApBuckets).catch(() => setApBuckets([]));
    financeApi.consolidationSummary().then(setConsolidation).catch(() => setConsolidation(null));
    financeApi.listConsolidationRuns().then(setConsolidationRuns).catch(() => setConsolidationRuns([]));
  }, []);

  async function download(reportType: string, format: 'PDF' | 'EXCEL') {
    const key = `${reportType}-${format}`;
    setExporting(key);
    try {
      const blob = await financeApi.exportReport(reportType, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType.toLowerCase()}.${format === 'PDF' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(url);
      notify.success(`Exported ${reportType} as ${format}`);
    } catch (e) {
      showApiError(e, 'Export failed');
    } finally {
      setExporting(null);
    }
  }

  async function runConsolidationClose() {
    const year = Number(fiscalYear);
    const period = Number(fiscalPeriod);
    if (!year || !period) {
      notify.error('Enter valid fiscal year and period');
      return;
    }
    setClosing(true);
    try {
      const run = await financeApi.runConsolidationClose(year, period);
      notify.success(`Consolidation ${run.runNumber} closed (${run.statusCode})`);
      setConsolidationRuns(await financeApi.listConsolidationRuns());
    } catch (e) {
      showApiError(e, 'Consolidation close failed');
    } finally {
      setClosing(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Financial Reports"
        description="P&L, balance sheet, cash flow, AR aging — PDF and Excel export"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Reports' }]}
        actions={
          <div className="flex gap-2 flex-wrap items-center">
            <Link to="/finance/ledger" className="btn btn-secondary btn-sm">
              Trial balance
            </Link>
            <Link to="/finance/reports/builder" className="btn btn-secondary btn-sm">
              Report builder
            </Link>
            {(['PROFIT_LOSS', 'BALANCE_SHEET', 'CASH_FLOW', 'AR_AGING'] as const).flatMap((rt) =>
              (['PDF', 'EXCEL'] as const).map((fmt) => (
                <Button
                  key={`${rt}-${fmt}`}
                  variant="secondary"
                  loading={exporting === `${rt}-${fmt}`}
                  onClick={() => void download(rt, fmt)}
                >
                  {rt.replace('_', ' ')} {fmt}
                </Button>
              )),
            )}
          </div>
        }
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <PaginatedReportTable report={pl} title="Profit & Loss" />
        <PaginatedReportTable report={bs} title="Balance Sheet" />
        <PaginatedReportTable report={cf} title="Cash Flow" />
        <PaginatedReportTable report={ar} title="AR Aging" />
        <PaginatedReportTable report={consolidation} title="Consolidation summary" />
        <div className="card p-4">
          <h3 className="font-semibold mb-3">AP Aging</h3>
          {apBuckets.length === 0 ? (
            <p className="text-sm text-slate-500">No open payables. Vendor bills from Procurement appear here after posting.</p>
          ) : (
            <>
              <table className="w-full text-sm">
                <tbody>
                  {paginatedApBuckets.map((bucket) => (
                    <tr key={bucket.bucketCode} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-2">{bucket.bucketLabel}</td>
                      <td className="py-2 text-right font-mono">{bucket.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination {...apBucketsPaginationProps} hasRows={apBuckets.length > 0} />
            </>
          )}
        </div>
        <div className="card p-4 lg:col-span-2">
          <h3 className="font-semibold mb-3">Consolidation close</h3>
          <p className="text-sm text-slate-500 mb-3">
            Posts intercompany elimination journals for all posted IC transactions in the selected period.
          </p>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <Input type="number" label="Fiscal year" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} />
            <Input type="number" label="Period" value={fiscalPeriod} onChange={(e) => setFiscalPeriod(e.target.value)} />
            <Button variant="primary" loading={closing} onClick={() => void runConsolidationClose()}>
              Run consolidation close
            </Button>
          </div>
          {consolidationRuns.length > 0 && (
            <>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left">Run</th>
                  <th className="p-2 text-left">Period</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Journal</th>
                </tr>
              </thead>
              <tbody>
                {paginatedConsolidationRuns.map((run) => (
                  <tr key={run.id} className="border-t">
                    <td className="p-2">{run.runNumber}</td>
                    <td className="p-2">{run.fiscalYear}-{run.fiscalPeriod}</td>
                    <td className="p-2">{run.statusCode}</td>
                    <td className="p-2">{run.eliminationJournalId ? `#${run.eliminationJournalId}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination {...consolidationRunsPaginationProps} hasRows={consolidationRuns.length > 0} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
